import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryIcon from "@mui/icons-material/History";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import { Avatar, Box, Button, Chip, IconButton, Menu, MenuItem, Popover, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";

import { colors } from "@components/ui/colors";

import { BaseModalComponent } from "@components/common/BaseModalComponent";
import { ClientNoteHistoryContent } from "./ClientNoteHistoryContent";
import { formatRelativeDate, NOTE_TYPES } from "./notesUtils";

export const ClientNoteCard = ({ note, isMine, isEditing, onEdit, onSave, onCancel, onDelete, onTogglePin }) => {
  const [draft, setDraft] = useState(note.content);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteConfirmAnchorEl, setDeleteConfirmAnchorEl] = useState<HTMLElement | null>(null);
  const historyTriggerRef = useRef<HTMLButtonElement | null>(null);

  const handleOpenHistory = (event?: { stopPropagation?: () => void }) => {
    event?.stopPropagation?.();
    historyTriggerRef.current?.click();
  };

  useEffect(() => {
    if (isEditing) {
      setDraft(note.content);
    }
  }, [isEditing, note.content]);

  const config = NOTE_TYPES[note.type] || NOTE_TYPES.general;

  const author = note.author || {
    name: "Team Member",
    avatar: null,
    role: "",
  };

  const authorInitials = author.name
    ?.split(" ")
    .filter(Boolean)
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleSave = () => {
    const value = draft.trim();

    if (!value) return;

    onSave?.(value, note.type || "general");
  };

  return (
    <Box
      sx={{
        position: "relative",
        border: "1px solid",
        borderColor: note.pinned ? colors.orange[300] : "transparent",
        backgroundColor: note.pinned ? colors.orange[50] : "var(--fx-surface-base, #FFFFFF)",
        borderRadius: 2.5,
        p: 1.5,
        transition: "all 0.2s ease",
      }}
    >
      {note.pinned && (
        <Box
          sx={{
            position: "absolute",
            top: -10,
            right: 16,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 10,
            backgroundColor: colors.orange[500],
            color: "#fff",
            fontSize: 9,
            fontWeight: 700,
            textTransform: "uppercase",
          }}
        >
          <PushPinIcon
            sx={{
              fontSize: 10,
            }}
          />
          Pinned
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1,
            minWidth: 0,
          }}
        >
          <Avatar
            src={author.avatar || undefined}
            sx={{
              width: 28,
              height: 28,
              minWidth: 28,

              backgroundColor: colors.orange[500],

              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            {authorInitials || "TM"}
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.gray[900],
                }}
              >
                {author.name}
              </Typography>

              {isMine && (
                <Chip
                  label="YOU"
                  size="small"
                  sx={{
                    height: 19,
                    fontSize: 9,
                    fontWeight: 700,
                    color: colors.orange.base,
                    backgroundColor: colors.orange[50],
                    border: `1px solid ${colors.orange[200]}`,
                  }}
                />
              )}

              <Chip
                label={config.label}
                size="small"
                sx={{
                  height: 20,
                  borderRadius: 1,
                  fontSize: 10,
                  fontWeight: 600,
                  backgroundColor: config.bg,
                  color: config.color,
                }}
              />
            </Box>

            <Box
              sx={{
                mt: 0.25,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexWrap: "wrap",
              }}
            >
              {author.role && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 10.5,
                      color: colors.gray[500],
                    }}
                  >
                    {author.role}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.gray[300],
                    }}
                  >
                    ·
                  </Typography>
                </>
              )}

              <Typography
                variant="caption"
                sx={{
                  fontSize: 10.5,
                  color: colors.gray[500],
                }}
              >
                {formatRelativeDate(note.createdAt)}
              </Typography>

              {(Boolean(note.updatedAt) || Boolean(note.history?.length)) && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.gray[300],
                    }}
                  >
                    ·
                  </Typography>

                  <Tooltip title="View edit history" arrow placement="top">
                    <Box
                      component="button"
                      type="button"
                      onClick={handleOpenHistory}
                      sx={{
                        all: "unset",
                        fontSize: 10.5,
                        color: colors.gray[400],
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.3,
                        transition: "color 0.15s ease",
                        "&:hover": {
                          color: colors.orange[500],
                          textDecoration: "underline",
                        },
                      }}
                    >
                      <HistoryIcon sx={{ fontSize: 12 }} />
                      edited
                    </Box>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {!isEditing && (
          <Box
            className="note-actions"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              flexShrink: 0,
            }}
          >
            <Tooltip title={note.pinned ? "Unpin note" : "Pin note"} arrow placement="top">
              <IconButton
                size="small"
                onClick={onTogglePin}
                sx={{
                  color: note.pinned ? colors.orange.base : colors.gray[500],
                  p: 0.5,
                  transition: "all 0.15s ease",
                  "&:hover": {
                    color: colors.orange.base,
                    backgroundColor: colors.orange[50],
                  },
                }}
              >
                {note.pinned ? (
                  <PushPinIcon
                    sx={{
                      fontSize: 17,
                      color: colors.orange.base,
                    }}
                  />
                ) : (
                  <PushPinOutlinedIcon
                    sx={{
                      fontSize: 17,
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Edit note" arrow placement="top">
              <IconButton
                size="small"
                onClick={onEdit}
                sx={{
                  color: colors.gray[500],
                  p: 0.5,
                  transition: "all 0.15s ease",
                  "&:hover": {
                    color: colors.gray[800],
                    backgroundColor: colors.gray[100],
                  },
                }}
              >
                <EditOutlinedIcon
                  sx={{
                    fontSize: 17,
                  }}
                />
              </IconButton>
            </Tooltip>

            <Tooltip title="More options" arrow placement="top">
              <IconButton
                size="small"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                  color: colors.gray[500],
                  p: 0.5,
                  transition: "all 0.15s ease",
                  "&:hover": {
                    color: colors.gray[800],
                    backgroundColor: colors.gray[100],
                  },
                }}
              >
                <MoreHorizIcon
                  sx={{
                    fontSize: 18,
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          pl: 4.75,
          mt: 1,
        }}
      >
        {isEditing ? (
          <Box>
            <TextField
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              size="small"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  onCancel?.();
                }

                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  handleSave();
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: 13,
                  borderRadius: 2,
                },
              }}
            />

            <Box
              sx={{
                display: "flex",
                gap: 1,
                mt: 1,
              }}
            >
              <Button
                size="small"
                variant="contained"
                disabled={!draft.trim()}
                onClick={handleSave}
                sx={{
                  textTransform: "none",
                  backgroundColor: colors.orange[500],
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: colors.orange[600],
                    boxShadow: "none",
                  },
                }}
              >
                Save
              </Button>

              <Button
                size="small"
                onClick={onCancel}
                sx={{
                  textTransform: "none",
                  color: colors.gray[600],
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: 13,
              lineHeight: 1.55,
              color: colors.gray[700],
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            {note.content}
          </Typography>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            minWidth: 160,
            borderRadius: 2,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            onEdit?.();
            setAnchorEl(null);
          }}
          sx={{
            gap: 1,
            fontSize: 13,
          }}
        >
          <EditOutlinedIcon
            sx={{
              fontSize: 17,
            }}
          />
          Edit Note
        </MenuItem>

        <MenuItem
          onClick={(event) => {
            handleOpenHistory(event);
            setAnchorEl(null);
          }}
          sx={{
            gap: 1,
            fontSize: 13,
            color: colors.gray[700],
          }}
        >
          <HistoryIcon
            sx={{
              fontSize: 17,
              color: colors.gray[500],
            }}
          />
          View History {note.history?.length ? `(${note.history.length})` : ""}
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            setDeleteConfirmAnchorEl(e.currentTarget);
            setAnchorEl(null);
          }}
          sx={{
            gap: 1,
            fontSize: 13,
            color: colors.red?.[500] || "#DC2626",
          }}
        >
          <DeleteOutlineIcon
            sx={{
              fontSize: 17,
            }}
          />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Popover (Minimalist style) */}
      <Popover
        open={Boolean(deleteConfirmAnchorEl)}
        anchorEl={deleteConfirmAnchorEl}
        onClose={() => setDeleteConfirmAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: 260,
            borderRadius: 3,
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)", mb: 0.75 }}>Delete note?</Typography>
        <Typography sx={{ fontSize: 11.5, color: "var(--fx-surface-textsecondary, #64748B)", lineHeight: 1.45, mb: 2 }}>
          Are you sure you want to delete this note? This action cannot be undone.
        </Typography>
        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
          <Button
            size="small"
            onClick={() => setDeleteConfirmAnchorEl(null)}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "none",
              color: "var(--fx-surface-textsecondary, #64748B)",
              borderRadius: 1.5,
              py: 0.4,
              px: 1.25,
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              onDelete?.();
              setDeleteConfirmAnchorEl(null);
            }}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: "#dc2626",
              color: "white",
              borderRadius: 2,
              boxShadow: "none",
              py: 0.5,
              px: 1.75,
              "&:hover": {
                bgcolor: "#b91c1c",
                boxShadow: "none",
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Popover>

      <BaseModalComponent
        modalKey={`client-note-history-${note.uid}`}
        modalLabel="Note History"
        defaultSizes={{
          width: { xs: "95%", sm: "580px", md: "620px" },
          height: { xs: "90%", sm: "640px", md: "680px" },
        }}
        allowInsidePreventModal
        preventParentTrigger
        elementTrigger={
          <button ref={historyTriggerRef} type="button" style={{ position: "absolute", left: -99999, top: -99999, visibility: "hidden" }} />
        }
      >
        <ClientNoteHistoryContent note={note} />
      </BaseModalComponent>
    </Box>
  );
};
